/**
 * Groups API (Phase 12 — web cold-start invite flow).
 *
 * Two operations, both on the signed-in-or-not web client:
 *  1. getGroupInvitePreview — fetches a DISPLAY-ONLY preview of the group for the
 *     landing and hand-off pages (pre-login). Deliberately NOT through the
 *     Firebase SDK; see the header on the fetch below.
 *  2. writePendingGroupJoin — after the user verifies their phone, records the
 *     intent to join on their OWN user doc. The JOIN itself happens IN THE APP on
 *     next login (the app has App Check and calls groupsJoinByInvite). Web never
 *     joins directly.
 */
import firebase from '../services/firebase';
import { buildPendingGroupJoin } from '../containers/join/inviteHelpers';

const FUNCTIONS_REGION = 'us-central1';

/**
 * WHY A RAW FETCH INSTEAD OF httpsCallable
 *
 * The Firebase functions SDK builds every callable request through
 * ContextProvider.getContext(), which awaits an App Check token before the
 * request is allowed to leave the browser. src/services/firebase.js activates
 * App Check with auto-refresh at module scope, so on a cold visit that means
 * loading reCAPTCHA v3, rendering an invisible widget and doing a token
 * exchange FIRST. Measured on prod (2026-09-03): the preview request started 2ms
 * after that exchange finished, 2.2s and 3.8s into the page, and the exchange
 * itself returned 400 both times — so the call went out unauthenticated anyway.
 *
 * groupsGetInvitePreview requires neither auth nor App Check by design: its
 * intended caller IS a stranger holding an unguessable invite link, pre-login,
 * and it returns only names, emoji and colors (splyt-api
 * src/controllers/groups.ts, groupsGetInvitePreview header comment). So the wait
 * bought nothing and cost the invitee seconds of spinner.
 *
 * IF THAT EVER CHANGES: re-enabling enforceAppCheck on groupsGetInvitePreview
 * breaks this call and the web invite landing with it. Change both sides
 * together.
 */
function callInvitePreview(gid, inviter) {
  const projectId = process.env.REACT_APP_PROJECT_ID;
  const url = `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net/groupsGetInvitePreview`;
  const data = inviter ? { gid, inviter } : { gid };

  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data }),
  }).then((res) => res.json().then((body) => unwrapCallable(body, res)));
}

/** Callable protocol: `{ result }` on success, `{ error: { message, status } }` on failure. */
function unwrapCallable(body, res) {
  if (body && body.error) {
    const err = new Error(body.error.message || 'Failed to load invite preview.');
    err.code = body.error.status;
    throw err;
  }
  if (!res.ok) throw new Error(`Invite preview failed (${res.status}).`);
  return body ? body.result : undefined;
}

const previewCache = new Map();

const cacheKey = (gid, inviter) => `${gid}|${inviter || ''}`;

/**
 * Fetch the display-only invite preview for a group.
 *
 * Cached per gid+inviter for the life of the page, so the hand-off screen reuses
 * what the landing already fetched instead of paying a second round trip — every
 * step between them is a client-side route change. Also adopts the request that
 * public/index.html starts at parse time, well before this bundle has loaded.
 *
 * @param {string} gid - the group id from /groups/join/{gid}
 * @param {string} [inviter] - the inviter uid from ?inviter= (optional)
 * @returns {Promise<Object>} { found:false } | { found:true, groupName, emoji,
 *   accent, inviter:{name,color}|null, members:[{name,color}], memberCount }
 */
export async function getGroupInvitePreview(gid, inviter) {
  const key = cacheKey(gid, inviter);
  if (previewCache.has(key)) return previewCache.get(key);

  // The inline prefetch in index.html races ahead of this bundle; adopt its
  // in-flight request when it is for the same invite.
  const prefetched = typeof window !== 'undefined' ? window.__splytInvitePreview : undefined;
  const promise =
    prefetched && prefetched.key === key
      ? prefetched.promise.then((body) => unwrapCallable(body, { ok: true, status: 200 }))
      : callInvitePreview(gid, inviter);

  // A failed preview must be retryable (the caller shows an "unavailable" state),
  // so drop it from the cache rather than caching the rejection forever.
  const guarded = promise.catch((err) => {
    previewCache.delete(key);
    throw err;
  });
  previewCache.set(key, guarded);
  return guarded;
}

/**
 * Record a pending group-join on the signed-in user's own doc. Merge so we never
 * clobber the rest of the profile (onUserCreated also merges, so order is safe).
 * @param {string} uid - the just-authenticated user's uid
 * @param {string} gid - the group id to join
 * @param {string} [inviter] - the inviter uid (optional)
 * @param {string} [inviteCode] - the short link code from ?i= (optional). Lands as
 *   users/{uid}.pendingGroupJoin.inviteCode so the app can stamp invite_id on group_joined.
 */
export async function writePendingGroupJoin(uid, gid, inviter, inviteCode) {
  const pendingGroupJoin = buildPendingGroupJoin(
    gid,
    inviter,
    firebase.firestore.FieldValue.serverTimestamp(),
    inviteCode,
  );
  return firebase
    .firestore()
    .collection('users')
    .doc(uid)
    .set({ pendingGroupJoin }, { merge: true });
}
